/**
 * Finish an OAuth login, signing in to the account's known sub-org when
 * Turnkey's own lookup cannot find one.
 *
 * Turnkey's login lookup does not see an audience registered after sign-up
 * (Apple's other platform added when the person connected Apple, or by the
 * post-login repair). For such a token the kit's completion falls through to
 * sign-up and mints an empty sub-organization the Morfi session stamp then
 * refuses. When the backend already links the identity to an account
 * (`knownOrganizationId`) and Turnkey finds none, sign in to that sub-org
 * directly; Turnkey still verifies the token against its registered
 * providers. Otherwise the kit's own completion runs unchanged, so accounts
 * whose identity Turnkey does find (including in another sub-org) keep the
 * existing path.
 *
 * Platform-neutral — both kits' `httpClient` and `loginWithOauth` fit.
 */

export interface AccountLookupClient {
  readonly proxyGetAccount: (input: {
    readonly filterType: 'OIDC_TOKEN';
    readonly filterValue: string;
  }) => Promise<{ readonly organizationId?: string | undefined } | undefined>;
}

export async function completeKnownLogin(input: {
  readonly oidcToken: string;
  readonly publicKey: string;
  readonly knownOrganizationId: string | undefined;
  readonly client: AccountLookupClient | undefined;
  readonly complete: () => Promise<unknown>;
  readonly loginWithOauth: (params: {
    oidcToken: string;
    publicKey: string;
    organizationId: string;
  }) => Promise<unknown>;
}): Promise<{ readonly completion: unknown; readonly viaKnownSubOrg: boolean }> {
  if (input.knownOrganizationId !== undefined && input.client !== undefined) {
    const found = await input.client.proxyGetAccount({
      filterType: 'OIDC_TOKEN',
      filterValue: input.oidcToken,
    });
    if (!found?.organizationId) {
      const completion = await input.loginWithOauth({
        oidcToken: input.oidcToken,
        publicKey: input.publicKey,
        organizationId: input.knownOrganizationId,
      });
      return { completion, viaKnownSubOrg: true };
    }
  }
  return { completion: await input.complete(), viaKnownSubOrg: false };
}
